import {
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Line,
  Tooltip,
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  SxProps,
  Theme,
  useTheme,
  Tooltip as MuiTooltip,
} from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AnalyticsGraphResponse } from "../../../../../models/Dashboard/Analytics";

export interface AppLineChartData {
  labels: string[];
  values: any[];
}

export function parseAnalyticsGraphDataToAppLineChartData(
  response: AnalyticsGraphResponse[]
): AppLineChartData {
  if (!response || response.length == 0) {
    return { labels: [], values: [] };
  }

  const labels: string[] = [];
  const line_chart_map = new Map<string, any>();

  for (let i in response) {
    const data_set = response[i];
    labels.push(data_set.label);

    for (let j in data_set.values) {
      const data = data_set.values[j];

      if (!line_chart_map.has(data.label)) {
        line_chart_map.set(data.label, { label: data.label });
      }

      const numericValue = Number(data.value);
      line_chart_map.get(data.label)[data_set.label] = isNaN(numericValue)
        ? 0
        : numericValue;
    }
  }

  return { labels: labels, values: Array.from(line_chart_map.values()) };
}

export interface LineChartComponentProps {
  title: string;
  message?: string;
  data: AppLineChartData;
  sx?: SxProps<Theme>;
  colors: string[];
  height?: number;
}

export default function LineChartComponent({
  title,
  message,
  data,
  sx,
  colors,
  height = 400,
}: LineChartComponentProps) {
  const theme = useTheme();

  const getNiceTicks = () => {
    if (!data.values.length)
      return { domain: [0, 100], ticks: [0, 25, 50, 75, 100] };

    let min = Infinity;
    let max = -Infinity;

    // Find min/max across all data series
    data.values.forEach((item) => {
      data.labels.forEach((label) => {
        const value = Number(item[label]);
        if (!isNaN(value)) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      });
    });

    // If all values are zero, use default range
    if (max === 0) return { domain: [0, 100], ticks: [0, 5, 10, 15, 20] };

    // Calculate nice rounded max value
    const exponent = Math.floor(Math.log10(max));
    const roundedMax =
      Math.ceil(max / Math.pow(10, exponent)) * Math.pow(10, exponent);

    // Calculate tick interval (aim for about 4-5 ticks)
    const interval = Math.ceil(roundedMax / 4 / 5) * 5;
    const niceMax = Math.ceil(roundedMax / interval) * interval;

    // Generate ticks
    const ticks = [];
    for (let i = 0; i <= niceMax; i += interval) {
      ticks.push(i);
    }

    return {
      domain: [0, niceMax],
      ticks: ticks,
    };
  };

  const { domain, ticks } = getNiceTicks();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper + "CC",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            padding: 1,
            boxShadow: theme.shadows[2],
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            {label}
          </CustomTypography>
          {payload.map((entry: any, index: number) => (
            <Box
              key={`tooltip-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                marginTop: 0.5,
                color: entry.color,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  backgroundColor: entry.color,
                  marginRight: 1,
                }}
              />
              <CustomTypography variant="body2">
                {entry.name}: {entry.value}
              </CustomTypography>
            </Box>
          ))}
        </Box>
      );
    }

    return null;
  };

  return (
    <Card>
      <CardContent sx={{ margin: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <CustomTypography
            variant="heading4"
            color={theme.palette.customColors.blackBorder}
            style={{ marginBottom: 24 }}
          >
            {title}
          </CustomTypography>

          {message && (
            <Box>
              <MuiTooltip title={message}>
                <InfoOutlinedIcon
                  sx={{ cursor: "pointer", color: theme.palette.grey[600] }}
                />
              </MuiTooltip>
            </Box>
          )}
        </Box>
        {data.values.length > 0 && (
          <Box sx={{ width: "100%", overflowY: "hidden", paddingBottom: 3 }}>
            <Box
              sx={{
                width:
                  data.values.length > 7
                    ? `${data.values.length * 100}px`
                    : "100%",
              }}
            >
              <ResponsiveContainer width="100%" height={height}>
                <LineChart
                  data={data.values}
                  margin={{ top: 0, right: 40, left: 12, bottom: 0 }}
                >
                  <XAxis
                    axisLine={false}
                    dataKey="label"
                    tick={{ fontSize:"0.8rem" }}
                  />
                  <YAxis
                    domain={domain}
                    ticks={ticks}
                    tick={{fontSize:"0.8rem" }}
                  />
                  <CartesianGrid horizontal={true} vertical={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="top"
                    align="left"
                    wrapperStyle={{ paddingBottom: "1rem", fontSize:"0.8rem" }}
                  />
                  {data.labels.map((item, index) => (
                    <Line
                      key={`line-${index}`}
                      type="linear"
                      dataKey={item}
                      stroke={colors[index]}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6, strokeWidth: 2 }}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}
        {data.values.length === 0 && (
          <Box
            sx={{
              width: "100%",
              height: { height },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 3.5,
            }}
          >
            <CustomTypography variant="heading8" color="textSecondary">
              Nothing to show for the selected filters
            </CustomTypography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
